/**
 * ModuleStats — Displays real-time module dashboard stats
 * Replaces static mock data with live backend data
 */
import { FileText, FolderOpen, Brain, Clock, ChevronRight } from 'lucide-react';
import { useModuleData } from '../../../hooks/useModuleData';

function StatCard({ icon: Icon, label, value, sub, color = 'var(--copper)' }) {
  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl p-4 flex items-center gap-4" data-testid={`stat-${label}`}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-2xl font-bold text-[var(--text-main)]">{value}</p>
        <p className="text-xs text-[var(--text-muted)]">{label}</p>
      </div>
      {sub && <span className="text-xs text-[var(--text-muted)] whitespace-nowrap">{sub}</span>}
    </div>
  );
}

function RecentItem({ title, date, status }) {
  const statusLabel = status === 'analyzed' ? 'Analysert' : 'Laster opp';
  const statusColor = status === 'analyzed' ? 'text-emerald-400' : 'text-yellow-400';
  return (
    <div className="flex items-center gap-3 p-3 bg-[var(--bg-highlight)]/50 rounded-lg">
      <div className="w-2 h-2 rounded-full bg-[var(--copper)]" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[var(--text-main)] truncate">{title}</p>
        {date && (
          <p className="text-xs text-[var(--text-muted)]">
            {new Date(date).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        )}
      </div>
      <span className={`text-xs font-medium ${statusColor}`}>{statusLabel}</span>
    </div>
  );
}

export function ModuleStats({ moduleId }) {
  const { data, loading } = useModuleData(moduleId);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-pulse" data-testid="module-stats-loading">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-20 bg-[var(--bg-highlight)] rounded-xl" />
        ))}
      </div>
    );
  }

  const stats = data?.stats;
  const recentDocs = data?.recent_documents || [];
  const recentFolders = data?.recent_folders || [];
  const hasActivity = recentDocs.length > 0 || recentFolders.length > 0;

  const docTotal = stats?.documents?.total || 0;
  const docAnalyzed = stats?.documents?.analyzed || 0;
  const docRecent = stats?.documents?.recent || 0;
  const foldersTotal = stats?.case_folders?.total || 0;
  const foldersAnalyzed = stats?.case_folders?.analyzed || 0;

  return (
    <div className="space-y-6" data-testid="module-stats">
      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={FileText} label="Dokumenter" value={docTotal} sub={docRecent > 0 ? `+${docRecent} siste 30d` : null} color="var(--copper)" />
        <StatCard icon={Brain} label="Analysert" value={docAnalyzed} color="#10b981" />
        <StatCard icon={FolderOpen} label="Saksmapper" value={foldersTotal} color="#3b82f6" />
        <StatCard icon={Clock} label="Mapper analysert" value={foldersAnalyzed} color="#8b5cf6" />
      </div>

      {/* Recent activity */}
      {hasActivity && (
        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-[var(--copper)]" />
            <h3 className="text-sm font-semibold text-[var(--text-main)]">Siste aktivitet</h3>
          </div>
          <div className="space-y-2">
            {recentDocs.map(doc => (
              <RecentItem key={doc.id} title={doc.filename} date={doc.created_at} status={doc.status} />
            ))}
            {recentFolders.map(folder => (
              <RecentItem key={folder.id} title={folder.name} date={folder.created_at} status={folder.status} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!hasActivity && docTotal === 0 && foldersTotal === 0 && (
        <div className="text-center py-8 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl">
          <FileText className="w-10 h-10 text-[var(--text-muted)] mx-auto mb-3 opacity-50" />
          <p className="text-sm text-[var(--text-muted)]">
            Ingen dokumenter eller saksmapper ennå. Last opp et dokument for å komme i gang.
          </p>
        </div>
      )}
    </div>
  );
}
